import React from 'react';
import { camelize } from 'humps';
import { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import { ApiResponse } from '@/types';
import { ERROR_UNEXPECTED } from '@/constants';

import { Alert } from '@/lib/ui/alert';

// Extracts and cleans pasted text by stripping whitespace.
export const getCleanedPastedText = (e: React.ClipboardEvent<HTMLInputElement>): string => {
  e.preventDefault();
  const pasted = e.clipboardData.getData('text');
  const cleaned = pasted.replace(/\s/g, '');
  return cleaned;
};

// Create strongly-typed form field names that return string literal types
export const createNameofProxy = <T extends Record<string, any>>() => {
  // -? - Minus optional modifier that removes optionality so the type is always defined
  // : K - Return the key as string literal type
  return new Proxy({} as { [K in keyof T]-?: K }, {
    get: (_target, prop) => prop,
  });
};

// Extracts only the dirty (modified) fields from form data
export const getDirtyFields = <T,>(
  data: T,
  dirtyFields: Partial<Record<keyof T, boolean | (boolean | undefined)[] | undefined>>,
): Partial<T> => {
  const dirtyData: Partial<T> = {};
  Object.keys(dirtyFields).forEach((key) => {
    const fieldKey = key as keyof T;
    if (dirtyFields[fieldKey]) {
      dirtyData[fieldKey] = data[fieldKey];
    }
  });

  return dirtyData;
};

// Maps server validation errors to react-hook-form errors
export const handleServerValidationErrors = <T extends FieldValues>(
  error: any,
  setError: UseFormSetError<T>,
): void => {
  if (error?.response) {
    const apiResponse = error.response.data as ApiResponse<unknown> | undefined;

    if (apiResponse?.errorMessage?.length && apiResponse?.errorField) {
      const camelCaseFieldName = camelize(apiResponse.errorField);
      setError(camelCaseFieldName as Path<T>, {
        type: 'server',
        message: apiResponse.errorMessage,
      });
      return;
    }
    Alert.error(apiResponse?.errorMessage || ERROR_UNEXPECTED);
  }
};
